import React, { useEffect, useRef, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useParams, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaArrowLeft,
  FaSyncAlt,
  FaExternalLinkAlt,
  FaEnvelope,
  FaBuilding,
  FaHandshake,
  FaCheckCircle,
  FaExclamationCircle,
  FaNewspaper,
  FaUserTie,
  FaClock,
  FaInfoCircle,
  FaPaperPlane,
  FaRobot,
  FaUser,
  FaSearch,
  FaTrashAlt,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechButton,
  TechChip,
  Empty,
  ErrorBox,
  Loading,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import {
  getHcdAuthorityProfile,
  regenerateHcdAuthorityProfile,
  askAuthority,
} from '../../services/monitoringService';

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCard = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  position: sticky;
  top: 1rem;
  align-self: start;
`;

const Avatar = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 10px;
  background: rgba(0, 180, 216, 0.08);
  border: 1px solid ${borderSoft};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img { width: 100%; height: 100%; object-fit: cover; }
  svg { color: ${accent}; font-size: 3.5rem; opacity: 0.4; }
`;

const HeroTitle = styled.h2`
  font-size: 1.25rem;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
`;

const HeroMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--text-secondary);

  .label {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const Block = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 1rem 1.1rem;
  margin-bottom: 0.9rem;

  h4 {
    font-family: ${mono};
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin: 0 0 0.7rem;
    display: flex;
    align-items: center;
    gap: 0.45rem;
    svg { color: ${accent}; }
  }

  p {
    color: var(--text-secondary);
    font-size: 0.92rem;
    line-height: 1.6;
    margin: 0;
    white-space: pre-wrap;
  }
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const Bullet = styled.li`
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.65rem 0.8rem;
  color: var(--text-secondary);
  font-size: 0.88rem;
  line-height: 1.5;
  display: flex;
  gap: 0.6rem;

  .marker {
    flex-shrink: 0;
    color: ${({ $tone }) =>
      $tone === 'pro' ? 'var(--alert-success)'
      : $tone === 'con' ? 'var(--alert-error)'
      : accent};
    margin-top: 3px;
  }

  .body {
    flex: 1;
    min-width: 0;
  }

  .evidence {
    margin-top: 4px;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.14em;
    color: var(--text-disabled);
    text-transform: uppercase;
  }
`;

const ConnectionItem = styled.li`
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.7rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  .head {
    color: var(--text-primary);
    font-size: 0.92rem;
    font-weight: 600;
  }
  .role {
    color: var(--text-secondary);
    font-size: 0.84rem;
  }
  .evidence {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.14em;
    color: var(--text-disabled);
    text-transform: uppercase;
  }
`;

const NewsRow = styled.a`
  display: flex;
  gap: 0.7rem;
  align-items: flex-start;
  padding: 0.65rem 0.8rem;
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  text-decoration: none;
  color: var(--text-secondary);
  transition: all 0.15s ease;
  margin-bottom: 0.45rem;

  &:hover {
    color: var(--text-primary);
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.04);
  }

  .icon { color: ${accent}; padding-top: 3px; flex-shrink: 0; }
  .body { flex: 1; min-width: 0; }
  .meta {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-top: 4px;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-decoration: none;
  margin-bottom: 0.85rem;

  &:hover { color: ${accent}; }
`;

const dotBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30%           { transform: translateY(-4px); opacity: 1; }
`;

const Cite = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 5px;
  margin: 0 2px;
  font-family: ${mono};
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1;
  color: ${({ $missing }) => ($missing ? 'var(--alert-error)' : accent)};
  background: ${({ $missing }) =>
    $missing ? 'rgba(239, 71, 111, 0.1)' : 'rgba(0, 180, 216, 0.13)'};
  border: 1px solid
    ${({ $missing }) =>
      $missing ? 'rgba(239, 71, 111, 0.4)' : 'rgba(0, 180, 216, 0.38)'};
  border-radius: 4px;
  cursor: ${({ $missing }) => ($missing ? 'help' : 'pointer')};
  vertical-align: baseline;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $missing }) =>
      $missing ? 'rgba(239, 71, 111, 0.18)' : 'rgba(0, 180, 216, 0.25)'};
    transform: translateY(-1px);
  }
`;

const PopupBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(8, 14, 24, 0.7);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

const PopupCard = styled.div`
  width: min(560px, 100%);
  background: var(--color-primary);
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 10px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(0, 180, 216, 0.08);
  padding: 1.1rem 1.25rem 1.25rem;
  position: relative;

  .close {
    position: absolute;
    top: 10px;
    right: 12px;
    background: transparent;
    border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 26px;
    height: 26px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.78rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease, border-color 0.15s ease;
    &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
  }

  .label {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    display: block;
    margin-bottom: 3px;
  }

  .idx {
    display: inline-block;
    font-family: ${mono};
    color: ${accent};
    font-size: 0.7rem;
    letter-spacing: 0.12em;
    background: rgba(0, 180, 216, 0.1);
    border: 1px solid rgba(0, 180, 216, 0.35);
    padding: 2px 8px;
    border-radius: 4px;
    margin-bottom: 0.6rem;
  }

  h3 {
    color: var(--text-primary);
    font-size: 1.02rem;
    margin: 0.1rem 0 0.55rem;
    line-height: 1.35;
  }

  .meta {
    font-family: ${mono};
    font-size: 0.66rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.7rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    align-items: center;

    .chip {
      background: rgba(11, 19, 32, 0.6);
      border: 1px solid ${borderSoft};
      padding: 3px 8px;
      border-radius: 4px;
    }
    .chip-low {
      color: var(--alert-warning, #f4a261);
      border-color: rgba(244, 162, 97, 0.4);
      background: rgba(244, 162, 97, 0.08);
    }
  }

  .extracto {
    color: var(--text-secondary);
    font-size: 0.86rem;
    line-height: 1.55;
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-left: 2px solid ${accent};
    border-radius: 6px;
    padding: 0.6rem 0.8rem;
    margin: 0 0 0.85rem;
    white-space: pre-wrap;
  }

  .open {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    color: ${accent};
    text-decoration: none;
    font-size: 0.86rem;
    padding: 0.45rem 0.75rem;
    border: 1px solid rgba(0, 180, 216, 0.35);
    border-radius: 6px;
    background: rgba(0, 180, 216, 0.06);
    transition: all 0.15s ease;
    &:hover {
      background: rgba(0, 180, 216, 0.18);
      transform: translateY(-1px);
    }
  }

  .missing {
    color: var(--alert-error);
    font-size: 0.85rem;
    padding: 0.55rem 0.7rem;
    background: rgba(239, 71, 111, 0.08);
    border: 1px solid rgba(239, 71, 111, 0.3);
    border-radius: 6px;
  }
`;

const ChatWrap = styled.div`
  margin-top: 1.5rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  padding: 0.85rem 1.1rem;
  border-bottom: 1px solid ${borderSoft};
  display: flex;
  align-items: center;
  gap: 0.7rem;
  background: rgba(0, 180, 216, 0.04);

  .title {
    flex: 1;
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.92rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    svg { color: ${accent}; }
  }
  .sub {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const ChatBody = styled.div`
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  max-height: 460px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 6px;
  }
`;

const ChatEmpty = styled.div`
  color: var(--text-disabled);
  font-size: 0.86rem;
  text-align: center;
  padding: 1.2rem 0.5rem;
  line-height: 1.55;

  .hint {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${accent};
    margin-top: 0.6rem;
    display: block;
  }
`;

const Msg = styled.div`
  display: flex;
  gap: 0.7rem;
  align-items: flex-start;

  .avatar {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${borderSoft};
    background: ${({ $role }) => ($role === 'user' ? 'rgba(0, 180, 216, 0.08)' : 'rgba(6, 214, 160, 0.08)')};
    color: ${({ $role }) => ($role === 'user' ? accent : 'var(--alert-success)')};
    font-size: 0.78rem;
  }

  .bubble {
    flex: 1;
    min-width: 0;
    background: rgba(11, 19, 32, 0.55);
    border: 1px solid ${borderSoft};
    border-radius: 8px;
    padding: 0.7rem 0.9rem;
    color: var(--text-secondary);
    font-size: 0.9rem;
    line-height: 1.55;

    p { margin: 0 0 0.5rem; }
    p:last-child { margin: 0; }
    ul, ol { margin: 0.4rem 0 0.4rem 1.2rem; padding: 0; }
    li { margin-bottom: 0.25rem; }
    code {
      font-family: ${mono};
      background: rgba(0, 180, 216, 0.1);
      color: ${accent};
      padding: 1px 5px;
      border-radius: 3px;
      font-size: 0.84rem;
    }
    h1, h2, h3, h4 {
      color: var(--text-primary);
      margin: 0.6rem 0 0.35rem;
      font-size: 0.96rem;
    }
    table {
      border-collapse: collapse;
      margin: 0.5rem 0;
      font-size: 0.84rem;
    }
    th, td {
      border: 1px solid ${borderSoft};
      padding: 4px 8px;
      text-align: left;
    }
    th { color: var(--text-primary); background: rgba(0, 180, 216, 0.06); }
    blockquote {
      border-left: 2px solid ${accent};
      padding-left: 0.7rem;
      margin: 0.4rem 0;
      color: var(--text-disabled);
    }
    a { color: ${accent}; }
  }

  .meta {
    margin-top: 4px;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const Typing = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent};
    animation: ${dotBounce} 1.1s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

const ChatInputWrap = styled.form`
  border-top: 1px solid ${borderSoft};
  padding: 0.75rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  background: rgba(11, 19, 32, 0.4);
`;

const ChatRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ChatInput = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 40px;
  max-height: 140px;
  background: rgba(11, 19, 32, 0.65);
  border: 1px solid ${borderSoft};
  border-radius: 7px;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.88rem;
  padding: 0.55rem 0.75rem;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &::placeholder { color: var(--text-disabled); }
  &:focus {
    border-color: rgba(0, 180, 216, 0.5);
    box-shadow: 0 0 0 2px rgba(0, 180, 216, 0.12);
  }
`;

const SendBtn = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid rgba(0, 180, 216, 0.35);
  background: rgba(0, 180, 216, 0.15);
  color: ${accent};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 180, 216, 0.3);
    transform: translateY(-1px);
  }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const ChatOptions = styled.div`
  display: flex;
  gap: 0.9rem;
  align-items: center;
  font-size: 0.78rem;
  color: var(--text-disabled);
  flex-wrap: wrap;

  .opt {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    cursor: pointer;
    user-select: none;
  }
  input[type="checkbox"] {
    width: 14px; height: 14px;
    accent-color: ${accent};
    cursor: pointer;
  }
  .clear {
    margin-left: auto;
    color: var(--text-disabled);
    background: transparent;
    border: 1px solid ${borderSoft};
    padding: 4px 9px;
    border-radius: 5px;
    font-size: 0.74rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
  }
`;

const SuggestPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.6rem;
`;

const SuggestPill = styled.button`
  background: transparent;
  border: 1px solid ${borderSoft};
  color: var(--text-secondary);
  font-size: 0.78rem;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    color: ${accent};
    border-color: rgba(0, 180, 216, 0.45);
    background: rgba(0, 180, 216, 0.06);
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--text-disabled);
  svg { color: ${accent}; }
`;

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; }
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase();
}

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

/* ============== Chat por autoridad ============== */

const SUGGESTIONS = [
  '¿Qué relación tiene con Manuel Passaglia?',
  '¿Cuáles son sus principales puntos débiles?',
  '¿En qué votaciones se diferenció del bloque?',
  '¿Tiene conexiones empresariales con el puerto?',
  '¿Cómo se posiciona sobre humedales y medio ambiente?',
];

// Regex que captura citas: [N1], [11], [fresh-3], [RAG-2].
// Captura el cluster completo cuando vienen juntas: [11][13][14].
const CITE_RE = /\[(N|fresh-|RAG-|rag-)?(\d+)\]/g;

function lookupSource(indexed, fresh, rag, prefix, num) {
  const id = num;
  const norm = String(prefix || '').toLowerCase();
  if (norm === 'fresh-') {
    return (fresh || []).find((s) => {
      const sid = String(s.idx || '').replace(/^fresh-/, '');
      return String(sid) === String(id);
    });
  }
  if (norm === 'rag-') {
    return (rag || []).find((s) => {
      const sid = String(s.idx || '').replace(/^RAG-/i, '');
      return String(sid) === String(id);
    });
  }
  // sin prefijo o N → indexed
  return (indexed || []).find((s) => {
    const sid = String(s.idx || '').replace(/^N/, '');
    return String(sid) === String(id);
  });
}

function renderWithCitations(node, onCite) {
  if (typeof node === 'string') {
    const parts = [];
    let last = 0;
    let m;
    CITE_RE.lastIndex = 0;
    while ((m = CITE_RE.exec(node)) !== null) {
      if (m.index > last) parts.push(node.slice(last, m.index));
      const prefix = m[1] || '';
      const num = m[2];
      parts.push({ __cite: true, prefix, num, raw: m[0] });
      last = m.index + m[0].length;
    }
    if (last < node.length) parts.push(node.slice(last));
    if (parts.length === 1 && typeof parts[0] === 'string') return node;
    return parts.map((p, i) => {
      if (typeof p === 'string') return p;
      const norm = String(p.prefix || '').toLowerCase();
      const label =
        norm === 'fresh-' ? `↗${p.num}` :
        norm === 'rag-' ? `◇${p.num}` :
        p.num;
      return (
        <Cite
          key={i}
          type="button"
          onClick={() => onCite({ prefix: p.prefix, num: p.num })}
          title={`Ver fuente ${p.raw}`}
        >
          {label}
        </Cite>
      );
    });
  }
  if (Array.isArray(node)) return node.map((n, i) => <React.Fragment key={i}>{renderWithCitations(n, onCite)}</React.Fragment>);
  if (React.isValidElement(node)) {
    const children = node.props.children;
    if (children === undefined || children === null) return node;
    return React.cloneElement(node, { ...node.props }, renderWithCitations(children, onCite));
  }
  return node;
}

function wrapWithCitations(onCite) {
  const wrap = (Tag) => ({ children, ...rest }) =>
    <Tag {...rest}>{renderWithCitations(children, onCite)}</Tag>;
  return {
    p: wrap('p'),
    li: wrap('li'),
    td: wrap('td'),
    th: wrap('th'),
    strong: wrap('strong'),
    em: wrap('em'),
    h1: wrap('h1'),
    h2: wrap('h2'),
    h3: wrap('h3'),
    h4: wrap('h4'),
    blockquote: wrap('blockquote'),
  };
}

function formatDateShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('es-AR', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

function CitePopup({ cite, indexed, fresh, rag, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!cite) return null;
  const source = lookupSource(indexed, fresh, rag, cite.prefix, cite.num);
  const norm = String(cite.prefix || '').toLowerCase();
  const label =
    norm === 'fresh-' ? `fresh-${cite.num}` :
    norm === 'rag-' ? `RAG-${cite.num}` :
    `N${cite.num}`;
  const isRag = norm === 'rag-';

  return (
    <PopupBackdrop onClick={onClose}>
      <PopupCard onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose} aria-label="Cerrar">×</button>
        <span className="idx">[{label}]</span>
        {source ? (
          <>
            <h3>{isRag ? (source.source_label || 'Chunk indexado') : (source.titulo || 'Sin título')}</h3>
            <div className="meta">
              {isRag && source.source_type && <span className="chip">tipo: {source.source_type}</span>}
              {isRag && typeof source.score === 'number' && (
                <span className="chip">score: {source.score.toFixed(3)}</span>
              )}
              {!isRag && source.fuente && <span className="chip">{source.fuente}</span>}
              {!isRag && source.fecha && <span className="chip">{formatDateShort(source.fecha)}</span>}
              {source.confianza && (
                <span className={`chip ${source.confianza === 'low' ? 'chip-low' : ''}`}>
                  Confianza: {source.confianza}
                </span>
              )}
              {norm === 'fresh-' && <span className="chip">Búsqueda en vivo</span>}
              {isRag && <span className="chip">Índice RAG (FTS + vector)</span>}
            </div>
            {source.extracto && <div className="extracto">{source.extracto}</div>}
            {source.url && (
              <a className="open" href={source.url} target="_blank" rel="noreferrer">
                Abrir fuente original <FaExternalLinkAlt style={{ fontSize: '0.72rem' }} />
              </a>
            )}
          </>
        ) : (
          <div className="missing">
            La cita <code>[{label}]</code> no pudo resolverse — el modelo citó un índice que no está en las fuentes devueltas. Probable error del LLM.
          </div>
        )}
      </PopupCard>
    </PopupBackdrop>
  );
}

function AuthorityChat({ slug, authorityName }) {
  const STORAGE_KEY = `arcana_authority_chat_${slug}`;
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [freshSearch, setFreshSearch] = useState(false);
  const [pending, setPending] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [lastSummary, setLastSummary] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const [activeSources, setActiveSources] = useState({ indexed: [], fresh: [], rag: [] });
  const bodyRef = useRef(null);

  const openCite = (cite, indexed, fresh, rag) => {
    setActiveSources({
      indexed: indexed || [],
      fresh: fresh || [],
      rag: rag || [],
    });
    setActiveCite(cite);
  };

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30))); } catch {}
  }, [messages, STORAGE_KEY]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, pending]);

  const send = async (questionOverride) => {
    const q = (questionOverride ?? input).trim();
    if (!q || pending) return;
    setChatError(null);
    const userMsg = { role: 'user', content: q, ts: Date.now() };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setPending(true);
    try {
      const res = await askAuthority({
        slug,
        question: q,
        history: next,
        freshSearch,
      });
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: res.answer || '(respuesta vacía)',
        ts: Date.now(),
        summary: res.context_summary,
        sources_indexed: res.sources_indexed || [],
        sources_fresh: res.sources_fresh || [],
        sources_rag: res.sources_rag || [],
      }]);
      setLastSummary(res.context_summary || null);
    } catch (e) {
      const detail = friendlyError(e, 'Error en la consulta');
      setChatError(detail);
    } finally {
      setPending(false);
    }
  };

  const clear = () => {
    setMessages([]);
    setLastSummary(null);
    setChatError(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <ChatWrap>
      <ChatHeader>
        <span className="title">
          <FaRobot /> Indagar sobre {authorityName}
        </span>
        {lastSummary && (
          <span className="sub">
            {lastSummary.noticias_indexadas} idx · {lastSummary.fresh_items || 0} fresh
          </span>
        )}
      </ChatHeader>
      <ChatBody ref={bodyRef}>
        {messages.length === 0 && !pending && (
          <ChatEmpty>
            Preguntale al asistente sobre votaciones, alianzas, antecedentes, conexiones empresariales o cualquier otra cosa que necesites para armar estrategia.
            <span className="hint">El chat ya tiene el perfil cargado. Si necesitás info reciente, activá "Buscar info nueva".</span>
            <SuggestPills>
              {SUGGESTIONS.map((s, i) => (
                <SuggestPill key={i} type="button" onClick={() => send(s)}>{s}</SuggestPill>
              ))}
            </SuggestPills>
          </ChatEmpty>
        )}
        {messages.map((m, i) => {
          const handleCite = (cite) => openCite(cite, m.sources_indexed, m.sources_fresh, m.sources_rag);
          return (
            <Msg key={i} $role={m.role}>
              <div className="avatar">{m.role === 'user' ? <FaUser /> : <FaRobot />}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="bubble">
                  {m.role === 'assistant' ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={wrapWithCitations(handleCite)}
                    >
                      {m.content}
                    </ReactMarkdown>
                  ) : (
                    m.content.split('\n').map((line, k) => <p key={k}>{line}</p>)
                  )}
                </div>
                {m.summary && (
                  <div className="meta">
                    Fuentes: {m.summary.noticias_indexadas} indexadas
                    {m.summary.fresh_search ? ` · ${m.summary.fresh_items} en vivo` : ''}
                    {m.summary.rag_chunks ? ` · ${m.summary.rag_chunks} RAG` : ''}
                    {(m.sources_indexed?.length || m.sources_fresh?.length || m.sources_rag?.length)
                      ? ' · click en [N] / [↗] / [◇] para ver detalle'
                      : ''}
                  </div>
                )}
              </div>
            </Msg>
          );
        })}
        {pending && (
          <Msg $role="assistant">
            <div className="avatar"><FaRobot /></div>
            <div className="bubble"><Typing><span /><span /><span /></Typing></div>
          </Msg>
        )}
        {chatError && (
          <ErrorBox style={{ margin: 0 }}>
            <FaExclamationCircle style={{ marginRight: 6 }} /> {chatError}
          </ErrorBox>
        )}
      </ChatBody>
      <ChatInputWrap onSubmit={(e) => { e.preventDefault(); send(); }}>
        <ChatRow>
          <ChatInput
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Preguntale sobre ${authorityName}… (Enter envía · Shift+Enter salto de línea)`}
            disabled={pending}
            rows={1}
          />
          <SendBtn type="submit" disabled={pending || !input.trim()} aria-label="Enviar">
            <FaPaperPlane />
          </SendBtn>
        </ChatRow>
        <ChatOptions>
          <label className="opt">
            <input
              type="checkbox"
              checked={freshSearch}
              onChange={(e) => setFreshSearch(e.target.checked)}
            />
            <FaSearch /> Buscar info nueva (SERPAPI + Google News)
          </label>
          {messages.length > 0 && (
            <button type="button" className="clear" onClick={clear}>
              <FaTrashAlt /> Limpiar
            </button>
          )}
        </ChatOptions>
      </ChatInputWrap>
      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={activeSources.indexed}
          fresh={activeSources.fresh}
          rag={activeSources.rag}
          onClose={() => setActiveCite(null)}
        />
      )}
    </ChatWrap>
  );
}

const AuthorityProfile = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [profileCite, setProfileCite] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getHcdAuthorityProfile(slug);
      setData(res);
    } catch (e) {
      setError(friendlyError(e, 'Error al cargar perfil'));
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    try {
      setRegenerating(true);
      setError(null);
      const res = await regenerateHcdAuthorityProfile(slug);
      setData(res);
    } catch (e) {
      setError(friendlyError(e, 'Error al regenerar'));
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (loading && !data) {
    return (
      <TechShell>
        <TechPageHeader title="Perfil de autoridad" subtitle="HCD San Nicolás" dossier="HCD-PROFILE" />
        <Loading>Cargando perfil…</Loading>
      </TechShell>
    );
  }

  if (error && !data) {
    return (
      <TechShell>
        <BackLink to="/monitoring/hcd"><FaArrowLeft /> Volver a Autoridades</BackLink>
        <TechPageHeader title="Perfil de autoridad" subtitle="HCD San Nicolás" dossier="HCD-PROFILE" />
        <ErrorBox>{error}</ErrorBox>
      </TechShell>
    );
  }

  const p = data?.profile;
  const prof = p?.profile || {};
  const news = asArray(p?.news);
  const sources = asArray(p?.sources);

  const openProfileCite = (cite) => setProfileCite(cite);
  // Render texto con citas [N] clickeables usando las fuentes del propio perfil.
  const cited = (text) => renderWithCitations(text, openProfileCite);
  // Lista de chips clickeables para campos "evidencia": [1, 2, 3]
  const evidenceChips = (arr) => (
    <>
      {asArray(arr).map((e, i) => (
        <Cite
          key={i}
          type="button"
          onClick={() => openProfileCite({ prefix: '', num: String(e) })}
          title={`Ver fuente [${e}]`}
          style={{ marginRight: 3 }}
        >
          {e}
        </Cite>
      ))}
    </>
  );

  return (
    <TechShell>
      <BackLink to="/monitoring/hcd"><FaArrowLeft /> Volver a Autoridades</BackLink>
      <TechPageHeader
        title={p?.nombre || 'Autoridad'}
        subtitle={[p?.cargo, p?.partido].filter(Boolean).join(' · ') || 'HCD San Nicolás'}
        dossier="HCD-PROFILE"
        actions={
          <TechButton onClick={regenerate} disabled={regenerating}>
            <FaSyncAlt style={{ marginRight: 6 }} />
            {regenerating ? 'Regenerando…' : 'Regenerar perfil'}
          </TechButton>
        }
      />

      <MetaRow style={{ margin: '0.6rem 0 1rem' }}>
        <FaClock />
        <span>Generado: {formatDate(p?.generated_at)} · {p?.news_count ?? 0} noticias indexadas · fuente: {data?.source || '—'}</span>
      </MetaRow>

      {p?.error_message && (
        <ErrorBox style={{ marginBottom: '1rem' }}>
          <FaInfoCircle style={{ marginRight: 8 }} />
          IA reportó: {p.error_message}
        </ErrorBox>
      )}

      <TwoCol>
        <HeroCard>
          <Avatar>
            {p?.foto ? (
              <img
                src={p.foto}
                alt={p?.nombre}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <span style={{ color: accent, fontFamily: mono, fontSize: '2.2rem' }}>
                {getInitials(p?.nombre)}
              </span>
            )}
          </Avatar>
          <HeroTitle>{p?.nombre}</HeroTitle>
          <HeroMeta>
            {p?.cargo && (<><span className="label">Cargo</span><span>{p.cargo}</span></>)}
            {p?.partido && (<><span className="label">Partido</span><span>{p.partido}</span></>)}
            {p?.periodo && (<><span className="label">Período</span><span>{p.periodo}</span></>)}
            {prof?.donde_vive && (<><span className="label">Reside en</span><span>{prof.donde_vive}</span></>)}
            {p?.email && (
              <>
                <span className="label">Email</span>
                <a href={`mailto:${p.email}`} style={{ color: accent, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <FaEnvelope /> {p.email}
                </a>
              </>
            )}
          </HeroMeta>
          {asArray(prof?.temas_principales).length > 0 && (
            <>
              <span style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>Temas principales</span>
              <Chips>
                {asArray(prof.temas_principales).map((t, i) => <TechChip key={i}>{t}</TechChip>)}
              </Chips>
            </>
          )}
        </HeroCard>

        <div>
          {prof?.bio_publico && (
            <Block>
              <h4><FaUserTie /> Bio pública</h4>
              <p>{cited(prof.bio_publico)}</p>
            </Block>
          )}

          {asArray(prof?.funciones_en_hcd).length > 0 && (
            <Block>
              <h4><FaBuilding /> Funciones en el HCD</h4>
              <BulletList>
                {asArray(prof.funciones_en_hcd).map((f, i) => (
                  <Bullet key={i}><span className="marker">›</span><div className="body">{cited(f)}</div></Bullet>
                ))}
              </BulletList>
            </Block>
          )}

          {asArray(prof?.trayectoria).length > 0 && (
            <Block>
              <h4><FaUserTie /> Trayectoria</h4>
              <BulletList>
                {asArray(prof.trayectoria).map((t, i) => (
                  <Bullet key={i}><span className="marker">›</span><div className="body">{cited(t)}</div></Bullet>
                ))}
              </BulletList>
            </Block>
          )}

          {asArray(prof?.conexiones_empresariales).length > 0 && (
            <Block>
              <h4><FaBuilding /> Conexiones empresariales</h4>
              <BulletList>
                {asArray(prof.conexiones_empresariales).map((c, i) => (
                  <ConnectionItem key={i}>
                    <span className="head">{c.entidad}</span>
                    <span className="role">{cited(c.rol)}</span>
                    {asArray(c.evidencia).length > 0 && (
                      <span className="evidence">Evidencia: {evidenceChips(c.evidencia)}</span>
                    )}
                  </ConnectionItem>
                ))}
              </BulletList>
            </Block>
          )}

          {asArray(prof?.conexiones_politicas).length > 0 && (
            <Block>
              <h4><FaHandshake /> Conexiones políticas</h4>
              <BulletList>
                {asArray(prof.conexiones_politicas).map((c, i) => (
                  <ConnectionItem key={i}>
                    <span className="head">{c.actor}</span>
                    <span className="role">{cited(c.vinculo)}</span>
                    {asArray(c.evidencia).length > 0 && (
                      <span className="evidence">Evidencia: {evidenceChips(c.evidencia)}</span>
                    )}
                  </ConnectionItem>
                ))}
              </BulletList>
            </Block>
          )}

          {asArray(prof?.fortalezas).length > 0 && (
            <Block>
              <h4><FaCheckCircle /> Pros (fortalezas accionables)</h4>
              <BulletList>
                {asArray(prof.fortalezas).map((f, i) => (
                  <Bullet key={i} $tone="pro">
                    <span className="marker"><FaCheckCircle /></span>
                    <div className="body">
                      {cited(f.punto || f)}
                      {asArray(f.evidencia).length > 0 && (
                        <div className="evidence">Evidencia: {evidenceChips(f.evidencia)}</div>
                      )}
                    </div>
                  </Bullet>
                ))}
              </BulletList>
            </Block>
          )}

          {asArray(prof?.debilidades).length > 0 && (
            <Block>
              <h4><FaExclamationCircle /> Contras (debilidades accionables)</h4>
              <BulletList>
                {asArray(prof.debilidades).map((d, i) => (
                  <Bullet key={i} $tone="con">
                    <span className="marker"><FaExclamationCircle /></span>
                    <div className="body">
                      {cited(d.punto || d)}
                      {asArray(d.evidencia).length > 0 && (
                        <div className="evidence">Evidencia: {evidenceChips(d.evidencia)}</div>
                      )}
                    </div>
                  </Bullet>
                ))}
              </BulletList>
            </Block>
          )}

          {prof?.apariciones_mediaticas_resumen && (
            <Block>
              <h4><FaNewspaper /> Apariciones mediáticas — resumen</h4>
              <p>{cited(prof.apariciones_mediaticas_resumen)}</p>
            </Block>
          )}

          <TechSection title={`Noticias relacionadas (${news.length})`}>
            {news.length === 0 ? (
              <Empty>No se encontraron menciones recientes (ventana 180 días).</Empty>
            ) : (
              <div>
                {news.map((n, i) => (
                  <NewsRow key={n.url || i} href={n.url} target="_blank" rel="noreferrer">
                    <FaNewspaper className="icon" />
                    <div className="body">
                      <div>[{i + 1}] {n.title}</div>
                      <div className="meta">
                        {n.source || 'Fuente desconocida'}
                        {n.published_at ? ` · ${formatDate(n.published_at)}` : ''}
                      </div>
                      {n.summary && (
                        <div style={{ marginTop: 5, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          {String(n.summary).slice(0, 240)}{String(n.summary).length > 240 ? '…' : ''}
                        </div>
                      )}
                    </div>
                    <FaExternalLinkAlt style={{ fontSize: '0.7rem', color: 'var(--text-disabled)' }} />
                  </NewsRow>
                ))}
              </div>
            )}
          </TechSection>

          {prof?.notas_metodologicas && (
            <Block>
              <h4><FaInfoCircle /> Notas metodológicas</h4>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-disabled)' }}>{cited(prof.notas_metodologicas)}</p>
            </Block>
          )}

          {sources.length > 0 && (
            <details style={{ marginTop: '1rem', color: 'var(--text-disabled)', fontFamily: mono, fontSize: '0.7rem' }}>
              <summary style={{ cursor: 'pointer' }}>Fuentes indexadas ({sources.length})</summary>
              <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                {sources.map((s) => (
                  <li key={s.idx}>
                    [{s.idx}] <a href={s.url} target="_blank" rel="noreferrer" style={{ color: accent }}>{s.titulo}</a>
                    {s.fuente ? ` — ${s.fuente}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </TwoCol>

      {p?.authority_key && (
        <AuthorityChat slug={p.authority_key} authorityName={p.nombre} />
      )}

      {profileCite && (
        <CitePopup
          cite={profileCite}
          indexed={sources}
          fresh={[]}
          rag={[]}
          onClose={() => setProfileCite(null)}
        />
      )}
    </TechShell>
  );
};

export default AuthorityProfile;
